import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from '../components/LegalLayout';
import { COMPANY } from '../lib/company';
import { useSEO } from '../hooks/useSEO';

const { emails } = COMPANY;

const sections = [
  {
    id: 'overview',
    title: 'Overview',
    body: (
      <p>
        This policy explains how to cancel IP9 plans and when you can get a refund. It forms part of our <Link to="/terms-of-service">Terms of Service</Link>. Your statutory rights are not affected. The Services are provided by{' '}
        {COMPANY.legalName}, {COMPANY.address}.
      </p>
    ),
  },
  {
    id: 'cancel',
    title: 'Cancelling a plan',
    body: (
      <ul>
        <li>You can switch off automatic renewal or cancel a plan at any time in the dashboard, or by emailing <a href={`mailto:${emails.support}`}>{emails.support}</a> from your account email address.</li>
        <li>Cancelling stops future renewals. You keep access until the end of the period you have paid for.</li>
        <li>You can close your account at any time. We handle your data on closure as described in our <Link to="/privacy-policy">Privacy Policy</Link>.</li>
      </ul>
    ),
  },
  {
    id: 'withdrawal',
    title: '14-day right of withdrawal (Consumers)',
    body: (
      <>
        <p>
          If you are a Consumer (an individual buying mainly for purposes outside your trade or business) in the UK or EU, you can withdraw from the contract within <strong>14 days</strong> of the day it was formed, without
          giving a reason.
        </p>
        <p>
          To withdraw, send us a clear statement of your decision before the 14 days end, by email to <a href={`mailto:${emails.support}`}>{emails.support}</a> or by post to {COMPANY.legalName}, {COMPANY.address}. You may use
          the model form below, but you do not have to.
        </p>
        <p>
          <strong>Effects of withdrawal.</strong> We will refund all payments received for the withdrawn contract without undue delay, and no later than 14 days after we receive your notice, using the same payment method you
          used unless you expressly agree otherwise. You will not pay any fee for the refund.
        </p>
        <p>
          Because proxy access starts as soon as you order, we ask at checkout for your express request to begin during the withdrawal period. If you made that request, we will deduct an amount proportionate to what was
          provided before you told us you were withdrawing (for bandwidth plans, the share of bandwidth used; for time-based plans, the share of the period elapsed). If the Services were fully provided within the 14 days at
          your request, the right of withdrawal no longer applies.
        </p>
      </>
    ),
  },
  {
    id: 'model-form',
    title: 'Model withdrawal form',
    body: (
      <div className="border border-slate-800 bg-slate-900/60 p-5 font-mono text-[13px] leading-relaxed text-slate-300">
        <p>(Complete and return this form only if you wish to withdraw from the contract.)</p>
        <p>
          To: {COMPANY.legalName} (IP9), {COMPANY.address}, {emails.support}
        </p>
        <p>I/We (*) hereby give notice that I/We (*) withdraw from my/our (*) contract for the supply of the following service: ____________________</p>
        <p>Ordered on (*) / received on (*): ____________________</p>
        <p>Name of consumer(s): ____________________</p>
        <p>Address of consumer(s): ____________________</p>
        <p>Account email: ____________________</p>
        <p>Signature of consumer(s) (only if this form is notified on paper): ____________________</p>
        <p>Date: ____________________</p>
        <p className="mb-0">(*) Delete as appropriate.</p>
      </div>
    ),
  },
  {
    id: 'refunds',
    title: 'Refunds for all customers',
    body: (
      <>
        <p>In addition to any statutory rights:</p>
        <ul>
          <li>
            If a plan has not been used at all, you can ask for a full refund within <strong>30 days</strong> of purchase.
          </li>
          <li>
            If a plan has been partly used, you can ask for a refund of the unused portion within <strong>30 days</strong> of purchase. We calculate it pro rata on bandwidth or time remaining.
          </li>
          <li>
            If the Services are not provided as described, or a fault on our side means you cannot use them, contact us. We will fix the problem or refund the affected part, whenever you bought the plan.
          </li>
        </ul>
        <p>
          Send refund requests to <a href={`mailto:${emails.support}`}>{emails.support}</a> with your account email and order reference. Approved refunds are paid to the original payment method within 14 days. Your bank or payment
          provider may take a few extra days to credit them.
        </p>
      </>
    ),
  },
  {
    id: 'exceptions',
    title: 'When we do not refund',
    body: (
      <ul>
        <li>Amounts for Services already used, except where the law or this policy says otherwise.</li>
        <li>Plans suspended or terminated because you seriously breached our <Link to="/acceptable-use-policy">Acceptable Use Policy</Link> (for example fraud or attacks), to the extent the law allows.</li>
        <li>Requests made after the 30-day period, unless there was a fault on our side or you have a statutory right to a refund.</li>
      </ul>
    ),
  },
  {
    id: 'chargebacks',
    title: 'Chargebacks',
    body: (
      <p>
        Please contact us before raising a chargeback with your bank. We can usually resolve billing problems quickly. We may suspend an account while a chargeback is open. This does not affect your rights under your card
        scheme or the law.
      </p>
    ),
  },
];

const RefundPolicyPage = () => {
  useSEO('refundPolicy');
  return <LegalLayout title="Refund & Cancellation Policy" sections={sections} />;
};

export default RefundPolicyPage;
